import { lazy, Suspense } from 'react'

import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  redirect,
  Route,
  RouterProvider,
  ScrollRestoration,
  type LoaderFunction,
} from 'react-router-dom'

import { ApolloProvider } from '@apollo/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { CookiesProvider } from 'react-cookie'

import { STAGING_WARNING } from 'shared/config'

import {
  CurrentUserProvider,
  canAccessEditorConsole,
  canAccessPollManager,
  canManagePolls,
  canManageTags,
  isAcceptedTermsOfUse,
} from 'shared/auth'

import { apollo, queryClient } from 'shared/api'

import { default as Layout } from 'app/layout'
import { default as ProtectedRoute } from 'app/protected-route'
import { default as SubstituteUserWarning } from 'app/substitute-user-warning'
import { default as SplashScreen } from 'shared/ui/splash-screen'
import { GetTextProvider } from 'shared/i18n'

// Layout-only imports — no data fetching, render immediately
import LandingLayout from 'pages/landing/layout'
import {
  Layout as GettingStartedLayout,
  AsContributor as GettingStartedAsContributor,
  AsCommenter as GettingStartedAsCommenter,
} from 'pages/landing/getting-started'

import { PollManagerLayout } from 'pages/poll-manager'
import { Dashboard, DashboardNavigate } from 'pages/dashboard'

import {
  EditorConsole,
  SubstituteUserContribute as EditorConsoleSubstituteUserContribute,
} from 'pages/editor-console'

import {
  Layout as SettingsLayout,
  ProfileSettings,
  AccountSettings,
} from 'pages/settings'

import { MediaLibrary } from 'pages/media-library'
import { Agreement as AgreementPage } from 'pages/agreement'
import { Contribute as ContributePage } from 'pages/contribute'

import {
  AnalyzePageLayout,
  PlotPageLayout as AnalyzePlotPageLayout,
  ChartSelect as AnalyzeChartSelect,
  SavedQueryList as AnalyzeSavedQueryList,
  SavedQuery as AnalyzeSavedQuery,
  BarChart as AnalyzeBarChart,
  Timeline as AnalyzeTimeline,
  ScatterPlot as AnalyzeScatterPlot,
  QuestionAnswersComparison as AnalyzeQuestionAnswersComparison,
  EntryAnswersComparison as AnalyzeEntryAnswersComparison,
} from 'pages/analyze'

import RootErrorBoundary from 'app/root-error-boundary'
import RouterErrorFallback from 'app/router-error-fallback'

import 'app/styles/tailwind.css'

// Pages without loaders — standard React.lazy
const AboutLayout = lazy(() => import('pages/landing/about/layout'))
const AboutTheDRHPage = lazy(() => import('pages/landing/about/about-the-drh'))
const VideosPage = lazy(() => import('pages/landing/about/videos'))
const NewsPage = lazy(() => import('pages/landing/about/news/news'))
const ImpactPage = lazy(() => import('pages/landing/about/impact/impact'))
const ResearchPage = lazy(() => import('pages/landing/about/research'))
const PedagogicalUsesPage = lazy(
  () => import('pages/landing/about/pedagogical-uses'),
)
const PeopleLayout = lazy(() => import('pages/landing/about/people/layout'))
const FundingPage = lazy(() => import('pages/landing/about/funding'))
const PollsLayout = lazy(() => import('pages/landing/polls/layout'))
const PollPage = lazy(() => import('pages/landing/polls/poll'))
const FaqPage = lazy(() => import('pages/landing/faq'))
const GetInvolvedPage = lazy(() => import('pages/landing/get-involved'))
const TermsOfUsePage = lazy(() => import('pages/landing/terms-of-use'))

const SignUpPage = lazy(() => import('pages/sign-up'))
const LoginPage = lazy(() => import('pages/login'))
const ForgotPasswordPage = lazy(() => import('pages/login/forgot-password'))
const ResetPasswordPage = lazy(() => import('pages/login/reset-password'))

const TagManger = lazy(() => import('pages/tag-manager/tag-manager'))

const PollManagerPollsPage = lazy(() => import('pages/poll-manager/polls'))
const PollManagerPollPage = lazy(() => import('pages/poll-manager/poll'))
const PollManagerDraftsPage = lazy(() => import('pages/poll-manager/drafts'))
const PollTranslationPage = lazy(
  () => import('pages/poll-manager/poll-translation'),
)
const DraftPage = lazy(() => import('pages/poll-manager/draft'))
const DraftHierarchyTab = lazy(
  () => import('pages/poll-manager/draft/hierarchy'),
)
const DraftAnswersTab = lazy(() => import('pages/poll-manager/draft/answers'))
const DraftGeneralTab = lazy(() => import('pages/poll-manager/draft/general'))
const DraftPreviewTab = lazy(() => import('pages/poll-manager/draft/preview'))

// Pages with GQL loaders — use React Router lazy so the loader's DocumentNode
// goes into the route chunk instead of main.js.

const landingHomeLazy = async () => {
  const [{ default: Component }, { default: loader }] = await Promise.all([
    import('pages/landing/home/home-page'),
    import('pages/landing/home/statistics-loader'),
  ])
  return { Component, loader }
}

const teamLazy = async () => {
  const [{ default: Component }, { default: loader }] = await Promise.all([
    import('pages/landing/about/people/team'),
    import('pages/landing/about/people/team/loader'),
  ])
  return { Component, loader }
}

const editorsLazy = async () => {
  const [{ default: Component }, { default: loader }] = await Promise.all([
    import('pages/landing/about/people/editors'),
    import('pages/landing/about/people/editors/loader'),
  ])
  return { Component, loader }
}

const expertsLazy = async () => {
  const [{ default: Component }, { default: loader }] = await Promise.all([
    import('pages/landing/about/people/experts'),
    import('pages/landing/about/people/experts/loader'),
  ])
  return { Component, loader }
}

const overviewOfPollsLazy = async () => {
  const [{ default: Component }, { default: loader }] = await Promise.all([
    import('pages/landing/polls/overview-of-polls'),
    import('pages/landing/polls/overview-of-polls/loader'),
  ])
  return { Component, loader }
}

const taggingTreesLazy = async () => {
  const [{ default: Component }, { default: loader }] = await Promise.all([
    import('pages/landing/polls/tagging-trees'),
    import('pages/landing/polls/tagging-trees/loader'),
  ])
  return { Component, loader }
}

const coverageLazy = async () => {
  const [{ default: Component }, { default: loader }] = await Promise.all([
    import('pages/coverage/coverage'),
    import('pages/coverage/loader'),
  ])
  return { Component, loader }
}

const visualizeLazy = async () => {
  const m = await import('pages/visualize')
  return { Component: m.default, loader: m.visualizeLoader }
}

const browseLayoutLazy = async () => {
  const { default: Component } = await import('pages/browse/layout/layout')
  return { Component }
}

const browseByPublicationDateLazy = async () => {
  const [{ default: Component }, { entriesByPublicationDateLoader: loader }] =
    await Promise.all([
      import('pages/browse/entries-by-publication-date'),
      import('pages/browse/entries-by-publication-date/loader'),
    ])
  return { Component, loader }
}

const browseByAuthorLazy = async () => {
  const [{ default: Component }, { entriesByAuthorLoader: loader }] =
    await Promise.all([
      import('pages/browse/entries-by-author'),
      import('pages/browse/entries-by-author/loader'),
    ])
  return { Component, loader }
}

const browseByRegionLazy = async () => {
  const [{ default: Component }, { entriesGroupedByRegionLoader: loader }] =
    await Promise.all([
      import('pages/browse/entries-by-region'),
      import('pages/browse/entries-by-region/loader'),
    ])
  return { Component, loader }
}

const browseByPollLazy = async () => {
  const [{ default: Component }, { entriesByPollLoader: loader }] =
    await Promise.all([
      import('pages/browse/entries-by-poll'),
      import('pages/browse/entries-by-poll/loader'),
    ])
  return { Component, loader }
}

const browseByDataSourceLazy = async () => {
  const [{ default: Component }, { entriesByDataSourceLoader: loader }] =
    await Promise.all([
      import('pages/browse/entries-by-data-source'),
      import('pages/browse/entries-by-data-source/loader'),
    ])
  return { Component, loader }
}

const browseByTagsGroupLazy = async () => {
  const [{ default: Component }, { entriesByTagsGroupLoader: loader }] =
    await Promise.all([
      import('pages/browse/entries-by-tags-group'),
      import('pages/browse/entries-by-tags-group/loader'),
    ])
  return { Component, loader }
}

// Redirects /browse/tags to the first available tag group.
const browseByTagsIndexLazy = async () => {
  const { default: entryTagGroupLoader } =
    await import('pages/browse/layout/entry-tag-group-loader')

  const loader: LoaderFunction = async ({ request }) => {
    const groups = await entryTagGroupLoader()

    if (groups.length === 0) {
      throw new Response('Not Found', { status: 404 })
    }

    const { search } = new URL(request.url)

    return redirect(`/browse/tags/${groups[0].id}${search}`)
  }

  return { loader }
}

const entrySearchLazy = async () => {
  const [{ default: EntrySearch }, { createEntrySearchLoader }] =
    await Promise.all([
      import('pages/browse/entry-search'),
      import('pages/browse/entry-search/loader'),
    ])
  return {
    Component: () => <EntrySearch pageSize={20} />,
    loader: createEntrySearchLoader(20),
  }
}

const browseEntryLazy = async () => {
  const [{ default: Component }, { default: loader }] = await Promise.all([
    import('pages/browse-entry/browse-entry'),
    import('pages/browse-entry/loader'),
  ])
  return { Component, loader }
}

const publicProfileLazy = async () => {
  const [{ default: Component }, { userIdByUsernameLoader: loader }] =
    await Promise.all([
      import('pages/public-profile/user'),
      import('pages/public-profile/loader'),
    ])
  return { Component, loader }
}

const questionnaireLazy = async () => {
  const [{ default: Component }, { default: loader }] = await Promise.all([
    import('pages/questionnaire/questionnaire'),
    import('pages/browse-entry/loader'),
  ])
  return { Component, loader }
}

const entryUpgradeLazy = async () => {
  const [{ default: Component }, { entryForUpgradeLoader: loader }] =
    await Promise.all([
      import('pages/entry-upgrade/page'),
      import('pages/entry-upgrade/entry'),
    ])
  return { Component, loader }
}

const translations = {
  fr: () =>
    import('shared/i18n/locales/fr.messages.json').then(
      (value) => value.default,
    ),
  'zh-hans': () =>
    import('shared/i18n/locales/zh-hans.messages.json').then(
      (value) => value.default,
    ),
  'zh-hant': () =>
    import('shared/i18n/locales/zh-hant.messages.json').then(
      (value) => value.default,
    ),
  ja: () =>
    import('shared/i18n/locales/ja.messages.json').then(
      (value) => value.default,
    ),
  es: () =>
    import('shared/i18n/locales/es.messages.json').then(
      (value) => value.default,
    ),
  vi: () =>
    import('shared/i18n/locales/vi.messages.json').then(
      (value) => value.default,
    ),
  th: () =>
    import('shared/i18n/locales/th.messages.json').then(
      (value) => value.default,
    ),
  hi: () =>
    import('shared/i18n/locales/hi.messages.json').then(
      (value) => value.default,
    ),
  ko: () =>
    import('shared/i18n/locales/ko.messages.json').then(
      (value) => value.default,
    ),
  pt: () =>
    import('shared/i18n/locales/pt.messages.json').then(
      (value) => value.default,
    ),
}

function RootRouteComponent() {
  return (
    <>
      <ScrollRestoration />
      <SubstituteUserWarning>
        <Outlet />
      </SubstituteUserWarning>
    </>
  )
}

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      errorElement={<RouterErrorFallback />}
      element={<RootRouteComponent />}
    >
      <Route element={<LandingLayout />}>
        <Route path="/get-involved" element={<GetInvolvedPage />} />
        <Route path="/getting-started" element={<GettingStartedLayout />}>
          <Route
            path="as-contributor"
            element={<GettingStartedAsContributor />}
          />
          <Route path="as-commenter" element={<GettingStartedAsCommenter />} />

          <Route index element={<Navigate to="as-contributor" replace />} />
        </Route>
      </Route>

      <Route path="/landing">
        <Route index lazy={landingHomeLazy} />

        <Route element={<LandingLayout />}>
          <Route path="about" element={<AboutLayout />}>
            <Route path="about-the-drh" element={<AboutTheDRHPage />} />
            <Route path="videos" element={<VideosPage />} />
            <Route path="drh-news" element={<NewsPage />} />
            <Route path="impact" element={<ImpactPage />} />
            <Route path="research" element={<ResearchPage />} />
            <Route path="pedagogy" element={<PedagogicalUsesPage />} />
            <Route path="people" element={<PeopleLayout />}>
              <Route index element={<Navigate to="team" replace />} />

              <Route path="team" lazy={teamLazy} />
              <Route path="editors" lazy={editorsLazy} />
              <Route path="experts" lazy={expertsLazy} />
            </Route>
            <Route path="funding" element={<FundingPage />} />

            <Route index element={<Navigate to="about-the-drh" replace />} />
          </Route>

          <Route path="terms-of-use" element={<TermsOfUsePage />} />

          <Route path="polls" element={<PollsLayout />}>
            <Route path=":pollId" element={<PollPage />} />
            <Route path="overview-of-polls">
              <Route index lazy={overviewOfPollsLazy} />
              <Route path=":categorySlug" lazy={overviewOfPollsLazy} />
            </Route>
            <Route path="tagging-trees">
              <Route index element={<Navigate to="0" replace />} />
              <Route path=":tab" lazy={taggingTreesLazy} />
            </Route>

            <Route
              index
              element={<Navigate to="overview-of-polls" replace />}
            />
          </Route>

          <Route path="faq" element={<FaqPage />} />
        </Route>
      </Route>

      <Route path="/editor-console/su/:targetUserId">
        <Route
          path="contribute"
          element={<EditorConsoleSubstituteUserContribute />}
        />

        <Route
          path="questionnaire/:entryId"
          element={<EditorConsoleSubstituteUserContribute />}
        />
      </Route>

      <Route element={<Layout />}>
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/login" element={<LoginPage />} />

        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset_password/:code" element={<ResetPasswordPage />} />

        <Route path="/browse">
          <Route lazy={browseLayoutLazy}>
            <Route index lazy={browseByPublicationDateLazy} />
            <Route path="authors" lazy={browseByAuthorLazy} />
            <Route path="regions" lazy={browseByRegionLazy} />
            <Route path="polls" lazy={browseByPollLazy} />
            <Route path="data-sources" lazy={browseByDataSourceLazy} />
            <Route path="tags">
              <Route index lazy={browseByTagsIndexLazy} />
              <Route path=":groupId" lazy={browseByTagsGroupLazy} />
            </Route>
            <Route path="search" lazy={entrySearchLazy} />
          </Route>

          <Route
            path="tags-group/:groupId"
            loader={({ params, request }) =>
              redirect(
                `/browse/tags/${params.groupId}${new URL(request.url).search}`,
              )
            }
          />

          <Route path=":entryId" lazy={browseEntryLazy} />
        </Route>

        <Route path="/visualize" lazy={visualizeLazy} />
        <Route path="/visualize/search" lazy={visualizeLazy} />
        <Route path="/visualize/history" lazy={visualizeLazy} />
        <Route path="/visualize/:searchQueryId" lazy={visualizeLazy} />

        <Route path="coverage" lazy={coverageLazy} />

        <Route element={<AnalyzePageLayout />}>
          <Route path="/analyze" element={<AnalyzeChartSelect />} />
          <Route element={<ProtectedRoute />}>
            <Route
              path="/analyze/saved-queries"
              element={<AnalyzeSavedQueryList />}
            />
          </Route>
        </Route>
        <Route element={<AnalyzePlotPageLayout />}>
          <Route path="/analyze/bar-chart" element={<AnalyzeBarChart />} />
          <Route path="/analyze/timeline" element={<AnalyzeTimeline />} />
          <Route
            path="/analyze/scatter-plot"
            element={<AnalyzeScatterPlot />}
          />
          <Route
            path="/analyze/question-answers-comparison"
            element={<AnalyzeQuestionAnswersComparison />}
          />
          <Route
            path="/analyze/entry-answers-comparison"
            element={<AnalyzeEntryAnswersComparison />}
          />
        </Route>
        <Route path="/analyze/:savedQueryId" element={<AnalyzeSavedQuery />} />

        <Route path="/accounts/:username" lazy={publicProfileLazy} />

        <Route
          element={
            <ProtectedRoute
              authorize={(user) => !isAcceptedTermsOfUse(user)}
              fallback={<Navigate to="/getting-started" />}
            />
          }
        >
          <Route path="/agreement" element={<AgreementPage />} />
        </Route>

        <Route
          element={
            <ProtectedRoute
              authorize={isAcceptedTermsOfUse}
              fallback={<Navigate to="/agreement" />}
            />
          }
        >
          <Route path="/settings" element={<SettingsLayout />}>
            <Route path="profile" element={<ProfileSettings />} />
            <Route path="account" element={<AccountSettings />} />

            <Route index element={<Navigate to="profile" replace />} />
          </Route>

          <Route
            path="/accounts/profile"
            element={<Navigate to="/settings" />}
          />

          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/media-library" element={<MediaLibrary />} />

          <Route path="/contribute" element={<ContributePage />} />

          <Route path="/questionnaire/:entryId" lazy={questionnaireLazy} />

          <Route path="/upgrade/:entryId" lazy={entryUpgradeLazy} />

          <Route
            path="/poll-manager"
            element={<ProtectedRoute authorize={canAccessPollManager} />}
          >
            <Route element={<PollManagerLayout />}>
              <Route path="polls" element={<PollManagerPollsPage />} />
              <Route
                path="drafts"
                element={<ProtectedRoute authorize={canManagePolls} />}
              >
                <Route index element={<PollManagerDraftsPage />} />
              </Route>
            </Route>

            <Route path="polls/:pollId" element={<PollManagerPollPage />} />
            <Route
              path="polls/:pollId/:language"
              element={<PollTranslationPage />}
            />

            <Route
              path="drafts/:draftId"
              element={<ProtectedRoute authorize={canManagePolls} />}
            >
              <Route element={<DraftPage />}>
                <Route path="hierarchy" element={<DraftHierarchyTab />} />
                <Route path="answers" element={<DraftAnswersTab />} />
                <Route path="general" element={<DraftGeneralTab />} />
                <Route path="preview" element={<DraftPreviewTab />} />

                <Route index element={<Navigate to="hierarchy" replace />} />
              </Route>
            </Route>

            <Route index element={<Navigate to="polls" />} />
          </Route>

          <Route
            path="/accounts/editor-console"
            element={<Navigate to="/editor-console" />}
          />

          <Route
            path="/editor-console"
            element={<ProtectedRoute authorize={canAccessEditorConsole} />}
          >
            <Route index element={<EditorConsole />} />
          </Route>

          <Route
            path="/accounts/dashboard"
            element={<Navigate to="/dashboard" />}
          />

          <Route
            path="/tag-manager"
            element={<ProtectedRoute authorize={canManageTags} />}
          >
            <Route index element={<TagManger />} />
          </Route>
        </Route>

        <Route index element={<DashboardNavigate />} />
      </Route>
    </Route>,
  ),
)

export default function App() {
  return (
    <RootErrorBoundary>
      <CookiesProvider>
        <ApolloProvider client={apollo}>
          <QueryClientProvider client={queryClient}>
            <GetTextProvider
              translations={translations}
              sourceLocale="en"
              fallback={<SplashScreen />}
            >
              <CurrentUserProvider fallback={<SplashScreen />}>
                <Suspense fallback={<SplashScreen />}>
                  {STAGING_WARNING && (
                    <div className="bg-red-400 py-1 text-center text-sm uppercase text-white">
                      Staging mode – changes won't be saved
                    </div>
                  )}
                  {/* The Suspense boundary above does NOT cover this: it
                      catches suspended renders (a route's `lazy` chunk), not
                      the data router waiting on loaders. On a cold visit to
                      any route with a loader -- /browse, /visualize -- the
                      router renders null until they resolve, which is a white
                      screen for as long as that takes (seconds, for the
                      heavier pages). fallbackElement is the router's own hook
                      for that window, and it renders only on the initial
                      load; later navigations keep showing the current page,
                      which is what pages/browse/layout relies on. Same
                      SplashScreen as every fallback above, so boot and first
                      route load look like one continuous state rather than
                      two different spinners. */}
                  <RouterProvider
                    router={router}
                    fallbackElement={<SplashScreen />}
                  />
                </Suspense>
              </CurrentUserProvider>
            </GetTextProvider>
          </QueryClientProvider>
        </ApolloProvider>
      </CookiesProvider>
    </RootErrorBoundary>
  )
}
